import React, {useState, useEffect, createContext, useContext, useCallback, useRef} from "react"
import {useOktaAuth} from "@okta/okta-react"

function useAgent(): [string, string, string?] | [] {
	const context = useContext(AgentContext)
	if (!context) {
		throw new Error("useAgent must be used within AgentProvider")
	}
	return context.agent
}

type AgentContextType = {
	agent: [string, string, string?] | []
}

const AgentContext = createContext<AgentContextType | null>(null)

const AgentProvider = ({children}: {children: React.ReactNode}) => {
	const [agent, setAgent] = useState<[string, string, string?] | []>([])
	const [fallbackAgent, setFallbackAgent] = useState<[string, string, string?] | []>([])
	const oktaObject = useOktaAuth()
	const oktaAuth = oktaObject?.oktaAuth
	const getOktaUserRef = useRef<(retry?: number) => Promise<void>>()

	useEffect(() => {
		const urlSearchParams = new URLSearchParams(window.location.search)
		const agentEmail = urlSearchParams.get("agent_email")
		const agentName = urlSearchParams.get("agent_name")

		if (agentEmail && agentName) {
			setFallbackAgent([agentEmail, agentName])
		}
	}, [])

	const getOktaUser = useCallback(
		async (retry = 0) => {
			try {
				const user = await oktaAuth?.getUser()
				if (user?.email && user?.name) {
					setAgent([user.email, user.name, user.sub])
				}
			} catch {
				if (retry < 3) {
					setTimeout(() => getOktaUserRef.current?.(retry + 1), 500)
				}
			}
		},
		[oktaAuth],
	)

	getOktaUserRef.current = getOktaUser

	useEffect(() => {
		getOktaUser()
	}, [getOktaUser])

	const agentValue = agent.length > 0 ? agent : fallbackAgent

	return <AgentContext.Provider value={{agent: agentValue}}>{children}</AgentContext.Provider>
}

export {useAgent, AgentProvider}
